import React, { Reducer, useCallback, useEffect, useReducer, useState } from "react"
import _ from "lodash"
import { Appearance, TextInput, TextStyle, View, ViewStyle } from "react-native"
import { Country, Region, Species } from "../../../database"
import { FastField, Field, Formik } from "formik"
import { FormControl, FormControlProps } from "../../form-control/form-control"
import { FormProps } from "../form"
import { ModelPicker, ModelPickerProps } from "../../model-picker/model-picker"
import { PhoneInput } from "../../input/phone-input/phone-input"
import { Text } from "../../text/text"
import { TxKeyPath, translate } from "../../../i18n"
import { color } from "../../../theme"
import { observer } from "mobx-react-lite"
import * as yup from "yup"
import { Checkbox } from "native-base"

const FORM_DROPDOWN: ViewStyle = {
  paddingHorizontal: 16,
  marginBottom: 8
}

const TEXT_NAME: TextStyle = {
  marginTop: 8
}

const TEXT_CONTAINER: ViewStyle = {
  paddingHorizontal: 16
}

const FORM_CONTROL: ViewStyle = {
  marginBottom: 4,
  paddingHorizontal: 16
}

const BORDER: ViewStyle = {
  height: 4,
  backgroundColor: color.background,
  marginTop: 10
}

const ACTION_SHEET_ITEM: ViewStyle = {
  minHeight: 44,
  flexDirection: "row",
  alignItems: "center",
  paddingHorizontal: 16,
}

export interface FirstPriceFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  // initialValues?: {
  //   name?: string
  //   phone?: number
  //   species?: string
  //   country?: string
  //   province?: string
  //   city?: string,
  // }
  user?: any
}

const CityPicker = (
  { name, city, province, colorScheme = Appearance.getColorScheme(), formControlProps, modelPickerProps }:
  { name: string, city?: Array<any>, province?: number, colorScheme?: "light" | "dark", formControlProps?: FormControlProps, modelPickerProps?: (Omit<ModelPickerProps, "onValueChange"> & { onValueChange?: (value, index, item) => void }) },
) => {
  const [ selectedCity, dispatchSelectedCity ] = useReducer(useCallback((prevState, action) => {
    switch (action.type) {
      case 'add':
        return _.uniq([...prevState, action.payload])
      case 'remove':
        return _.without(prevState, action.payload)
      case 'set':
        return [...action.payload]
      default:
        return prevState;
    }
  }, []), [...city])
  const [ regions, dispatchRegions ] = useReducer<Reducer<Array<Region>, any>>(useCallback((prevState, action) => {
    switch (action.type) {
      case 'set':
        return [...action.payload]
      default:
        return prevState;
    }
  }, []), [])
  const [ isFetchingRegions, setIsFetchingRegions ] = useState<boolean>(false)
  const [ allSelected, setAllSelected ] = useState<boolean>(false)
  const [ fetchedRegions, setFetchedRegions ] = useState<Array<any>>([])

  const cityJoined = (city || []).join(',');

  useEffect(() => {
    Region.fetch({
      province_id__eq: province,
      scope_only: 'regency',
    }).then((regions) => {
      const data = []
      regions.forEach((item) => data.push(item.id))
      setFetchedRegions(data)
    })
  }, [province])

  useEffect(() => {
    if (!city.join(',')) {
      dispatchRegions({ type: 'set', payload: [] })
      return;
    }
    setIsFetchingRegions(true)
    Region.fetch({
      id__in: city.join(',') || '-'
    }).then((regions) => {
      dispatchRegions({ type: 'set', payload: regions })
    }).catch(() => {
      dispatchRegions({ type: 'add', payload: [] })
    }).finally(() => {
      setIsFetchingRegions(false)
    })

  }, [city, cityJoined])

  const onSelectAll = useCallback(async () => {
    if (!allSelected) dispatchSelectedCity({ type: 'set', payload: fetchedRegions })
    else dispatchSelectedCity({ type: 'set', payload: [] })
    setAllSelected(!allSelected)
  }, [allSelected, fetchedRegions])

  const renderSelectedItemText = useCallback(() => {
    if (!city.length) return null;
    return regions.map((region: Region) => (region.getAttribute('name'))).join(', ');
  }, [city.length, regions])

  const renderItemText = useCallback((item) => {
    if (!item) return null;
    return item[modelPickerProps.dataLabel || 'name']
  }, [modelPickerProps.dataLabel])

  const headerList = useCallback(() => (
    <View style={ACTION_SHEET_ITEM}>
      <Checkbox
        value="one"
        onChange={onSelectAll}
        colorScheme="blue"
        defaultIsChecked={allSelected}
      >
        <Text tx="shrimpPriceScreen.selectAll" preset="body2" colorScheme={colorScheme} marginLeft="16px" />
      </Checkbox>
    </View>
  ), [allSelected, colorScheme, onSelectAll])

  return (
    <Field name={name}>
      {({ field, form: { setFieldValue, setFieldTouched }, meta }) => (
        <FormControl
          isInvalid={meta.touched && !!meta.error}
          labelTx={`shrimpPriceScreen.${_.camelCase(field.name)}` as TxKeyPath}
          labelPreset="title3"
          colorScheme={colorScheme}
          labelColor={color.palette.gray800}
          errorMessage={meta.error}
          style={FORM_DROPDOWN}
          isDisabled={isFetchingRegions}
          {...formControlProps}
        >
          <ModelPicker
            isDisabled={isFetchingRegions}
            colorScheme={colorScheme}
            placeholderTx={`shrimpPriceScreen.${_.camelCase(field.name)}Placeholder` as TxKeyPath}
            // value={field.value}
            onValueChange={(value) => {
              const exist = selectedCity.findIndex((i) => i === value)
              const key = exist < 0 ? 'add' : 'remove'
              if (key === 'remove') setAllSelected(false);
              dispatchSelectedCity({ type: key, payload: value })
              setFieldTouched(field.name)
            }}
            isUseSearch
            pickerActionsheetProps={{
              selectedChecked: selectedCity,
              isUseButton: true,
              colorScheme: colorScheme,
              buttonTx: 'shrimpPriceScreen.implement' as TxKeyPath,
              isDisabled: allSelected,
              onPressBottomButton: () => {
                setFieldValue(field.name, selectedCity)
                setFieldTouched(field.name)
              },
              flatListProps: {
                ListHeaderComponent: headerList,
                extraData: selectedCity
              },
            }}
            isCheckModal
            {...modelPickerProps}
            renderSelectedItemText={renderSelectedItemText}
            renderItemText={renderItemText}
          />
        </FormControl>
      )}
    </Field>
  )
}


const ModelField = (
  { name, colorScheme = Appearance.getColorScheme(), formControlProps, modelPickerProps }:
  { name: string, colorScheme?: "light" | "dark", formControlProps?: FormControlProps, modelPickerProps?: (Omit<ModelPickerProps, "onValueChange"> & { onValueChange?: (value, index, item) => void }) },
) => (
  <FastField name={name}>
    {({ field, form: { setFieldValue, setFieldTouched }, meta }) => (
      <FormControl
        isRequired
        isInvalid={meta.touched && !!meta.error}
        labelTx={`shrimpPriceScreen.${_.camelCase(field.name)}` as TxKeyPath}
        labelPreset="title3"
        colorScheme={colorScheme}
        labelColor={color.palette.gray800}
        errorMessage={meta.error}
        style={FORM_DROPDOWN}
        {...formControlProps}
      >
        <ModelPicker
          colorScheme={colorScheme}
          placeholderTx={`shrimpPriceScreen.${_.camelCase(field.name)}Placeholder` as TxKeyPath}
          value={field.value}
          onValueChange={(value) => {
            setFieldTouched(field.name)
            setFieldValue(field.name, value)
          }}
          isUseSearch={field.name !== 'species'}
          {...modelPickerProps}
        />
      </FormControl>
    )}
  </FastField>
)

const PhoneField = (
  { name, input = {}, colorScheme = Appearance.getColorScheme(), formControlProps, value = null, isValid = false, handleSubmit }:
  { name: string, input: any, colorScheme?: "light" | "dark", formControlProps?: FormControlProps, value?: string, isValid?: boolean, handleSubmit?: () => void },
) => (
  <FastField name="phone">
    {({ field, form: { handleChange, handleBlur }, meta }) => (
      <FormControl
        isRequired
        isInvalid={meta.touched && !!meta.error}
        labelTx={`shrimpPriceScreen.${_.camelCase(field.name)}` as TxKeyPath}
        labelPreset="title3"
        colorScheme={colorScheme}
        errorMessage={meta.error}
        style={FORM_CONTROL}
        {...formControlProps}
      >
        <PhoneInput
          colorScheme={colorScheme}
          isInvalid={meta.touched && !!meta.error}
          textProps={{
            testID: `jala:id/${field.name}-input`,
            ref: input[field.name],
            autoCapitalize: "none",
            autoComplete: "tel",
            textContentType: "telephoneNumber",
            onSubmitEditing: () => isValid && handleSubmit(),
            placeholderTx: `editProfileForm.${_.camelCase(field.name)}Placeholder` as TxKeyPath,
            defaultValue: value,
            onBlur: handleBlur(field.name),
          }}
          initialValue={value}
          onChangePhoneNumber={handleChange(field.name)}
        />
      </FormControl>
    )}
  </FastField>
)

const TextField = (name) => (
  <View style={TEXT_CONTAINER}>
    <Text preset="title3" tx="shrimpPriceScreen.name" color={color.palette.gray800} />
    <Text preset="title2" text={name} color={color.primary} style={TEXT_NAME} />
  </View>
)

/**
 * Describe your component here
 */
export const FirstPriceForm = observer(function FirstPriceForm(props: FirstPriceFormProps) {
  const { onSubmit, colorScheme = Appearance.getColorScheme(), innerRef, user, ...rest } = props

  const input = {
    phone: React.useRef<TextInput>()
  }

  const validationSchema = yup.object({
    phone: yup.string()
      .required().label(translate("registerForm.phone"))
      .min(8, translate("shrimpPriceScreen.phoneValidationLength"))
      .max(14, translate("shrimpPriceScreen.phoneValidationLength"))
  })

  return (
    <Formik
      // enableReinitialize
      initialValues={{
        name: user.name,
        phone: user.phone || '',
        species: 1,
        country: "ID",
        province: null,
        city: [],
      }}
      onSubmit={onSubmit}
      innerRef={innerRef}
      validationSchema={validationSchema}
      {...rest}
    >
       {({ values, setFieldValue, setFieldTouched}) => {
        return (
          <>
            {TextField(values.name)}
            <View style={BORDER} />
            <PhoneField name="phone" colorScheme={colorScheme} input={input} value={values.phone} />
            <ModelField
              name="species"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
              modelPickerProps={{ Model: Species, titleTx: "cycleForm.speciesIdPlaceholder" }}
            />
            <ModelField
              name="country"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
              modelPickerProps={{
                Model: Country,
                titleTx: "farmForm.countryIdPlaceholder",
                value: values.country,
                onValueChange: (value) => {
                  setFieldValue("city", [])
                  setFieldValue("province", null)
                  setFieldValue("country", value)
                  setFieldTouched("country")
                },
                fetchData: (model, search) => (
                  model.fetch({
                    search,
                    sort: 'name'
                  }).then((models) => (
                    _.sortBy(models, (m) => {
                      const index = ['ID', 'EC', 'VN'].findIndex((k) => k === m.id)
                      return index < 0 ? m.name : `${index}`
                    })
                  ))
                )
              }}
            />
            <ModelField
              key={`${values.country}-province`}
              name="province"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
              modelPickerProps={{
                Model: Region,
                titleTx: "shrimpPriceScreen.provinceLabel",
                value: values.province,
                onValueChange: (value) => {
                  setFieldValue("city", [])
                  setFieldValue("province", value)
                  setFieldTouched("province")
                },
                fetchData: (model, search) => {
                  return (
                    model.fetch({
                      country_id__eq: values.country,
                      scope_only: 'province',
                      search,
                      sort: 'name'
                    }).then((models) => {
                      return model.fetch({ id__eq: '99' }).then((ms) => [...models, ...ms])
                    })
                  )
                },
               }}
            />
            {['ID'].includes(values.country) && values.province && (<CityPicker
              key={`${values.province}-city`}
              name="city"
              city={values.city}
              colorScheme={colorScheme}
              province={values.province}
              modelPickerProps={{
                Model: Region,
                titleTx: "farmForm.regionIdPlaceholder",
                isUseSearch: false,
                fetchData: (model, search) => (
                  model.fetch({
                    province_id__eq: values.province,
                    scope_only: 'regency',
                    search,
                    sort: 'name'
                  })
                )
               }}
            />)}
          </>
        )
       }}
    </Formik>
  )
})

FirstPriceForm.displayName = 'FirstPriceForm'
